import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { CheckCircle2, Hammer, LayoutList, Rows3, Send, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { FollowupDetailSheet } from "@/components/followups/followup-detail-sheet";
import { FollowupsList } from "@/components/followups/followups-list";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { ActionItemRow, ActionItemStatus } from "@/hooks/use-action-items";
import { useActionItems, usePatchActionItem } from "@/hooks/use-action-items";
import { cn } from "@/lib/utils";

const FILTERS: ReadonlyArray<{ id: ActionItemStatus | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "sent", label: "Sent" },
  { id: "done", label: "Done" },
  { id: "dismissed", label: "Dismissed" },
];

const SKELETON_KEYS = ["a", "b", "c", "d"] as const;

export function FollowupsPage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const search = useSearch({ from: "/s/$solutionId/followups" });
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useActionItems(solutionId);
  const patch = usePatchActionItem(solutionId);
  const [filter, setFilter] = useState<ActionItemStatus | "all">("pending");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openItem, setOpenItem] = useState<ActionItemRow | null>(null);

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function setView(view: "row" | "tile") {
    navigate({
      to: "/s/$solutionId/followups",
      params: { solutionId },
      search: { view },
    });
  }

  function bulkPatch(p: { status: ActionItemStatus }, label: string) {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    Promise.all(ids.map((id) => patch.mutateAsync({ id, patch: p }))).then(
      () => {
        toast.success(`${label} ${ids.length} item${ids.length === 1 ? "" : "s"}`);
        clearSelection();
      },
      () => {
        // per-item error toasts already fire via the mutation
        clearSelection();
      },
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-6">
      <PageHeader
        title="Follow-ups"
        description="Action items extracted from sources. Queue them up here, then push to your Deliver project to hand off."
      />

      <div className="rounded-lg border border-nymbl-blue/30 bg-nymbl-sky/60 px-4 py-3 flex items-start gap-3">
        <Hammer className="h-4 w-4 text-nymbl-navy mt-0.5 shrink-0" />
        <div className="flex-1 text-sm text-nymbl-navy">
          <span className="font-semibold">Coming soon.</span> Follow-ups isn't ready for clients yet
          — what you see below is an early preview.
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex gap-1.5 flex-1 flex-wrap">
          {FILTERS.map((f) => {
            const isActive = filter === f.id;
            const count =
              f.id === "all" ? data?.length : data?.filter((i) => i.status === f.id).length;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  isActive
                    ? "bg-nymbl-navy text-white"
                    : "bg-muted text-foreground/70 hover:bg-muted/80",
                )}
              >
                {f.label}
                {count != null && <span className="ml-1.5 opacity-70 tabular-nums">{count}</span>}
              </button>
            );
          })}
        </div>

        <div className="inline-flex items-center rounded-md border border-[var(--color-border)] bg-card p-0.5 h-8">
          <button
            type="button"
            onClick={() => setView("row")}
            title="Compact rows"
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-[5px] transition-colors",
              search.view === "row"
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Rows3 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setView("tile")}
            title="Tile cards"
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-[5px] transition-colors",
              search.view === "tile"
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <LayoutList className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-10 flex items-center gap-2 rounded-lg border border-nymbl-blue bg-nymbl-sky/80 px-3 py-2 text-sm shadow-sm">
          <span className="font-display font-semibold text-nymbl-navy">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkPatch({ status: "done" }, "Marked done")}
              disabled={patch.isPending}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              Mark done
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkPatch({ status: "dismissed" }, "Dismissed")}
              disabled={patch.isPending}
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={() => bulkPatch({ status: "sent" }, "Pushed to Deliver:")}
              disabled={patch.isPending}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Push to Deliver
            </Button>
          </div>
        </div>
      )}

      {isLoading && (
        <ul className="space-y-2">
          {SKELETON_KEYS.map((k) => (
            <Skeleton
              key={k}
              className={cn("rounded-lg", search.view === "row" ? "h-[44px]" : "h-[110px]")}
            />
          ))}
        </ul>
      )}

      {isError && <ErrorState message="Failed to load action items" onRetry={() => refetch()} />}

      {data && (
        <FollowupsList
          solutionId={solutionId}
          items={data}
          filter={filter}
          view={search.view}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onOpenItem={setOpenItem}
        />
      )}

      <FollowupDetailSheet
        open={openItem !== null}
        onOpenChange={(open) => {
          if (!open) setOpenItem(null);
        }}
        solutionId={solutionId}
        item={openItem}
      />
    </div>
  );
}
